(function () {
  'use strict';
  const property = '_mode_checkout_ref';
  // The customer's choice for a cart with Brisbane stock and Supplier Order
  // goods, and the whole-cart manifest the rate callback needs to price it.
  const dispatchProperty = '_mode_dispatch';
  const manifestProperty = '_mode_cart';
  const adapterOwned = [property, dispatchProperty, manifestProperty];
  const policies = ['now', 'together'];
  const storageKey = 'mode-shipping-dispatch';
  const pattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const bundleLine = item => Boolean(item.has_components || item.parent_relationship || item.item_components?.length);
  const shippableLine = item => item.requires_shipping !== false && !bundleLine(item);
  const compareVariants = (a, b) => a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);
  const canonical = value => JSON.stringify(value, function (_, item) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
    return Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]]));
  });

  function validateCart(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length > 100 || typeof cart.token !== 'string') {
      throw new Error('invalid_cart');
    }
    for (const item of cart.items) {
      if (typeof item.key !== 'string' || !Number.isSafeInteger(item.quantity) || item.quantity < 1
        || !Number.isSafeInteger(item.variant_id) || item.variant_id < 1
        || (item.properties && (typeof item.properties !== 'object' || Array.isArray(item.properties)))) {
        throw new Error('invalid_cart');
      }
      if (Object.entries(item.properties || {}).some(([key, value]) => key.length > 255 || typeof value !== 'string'
        || value.length > 2048) || Object.keys(item.properties || {}).length > 100) throw new Error('invalid_cart');
    }
    return cart;
  }

  function signature(cart, includeReference) {
    const lines = new Map();
    for (const item of cart.items) {
      const properties = {...item.properties};
      if (!includeReference) for (const key of adapterOwned) delete properties[key];
      const key = canonical({variant: item.variant_id, properties, shipping: item.requires_shipping !== false,
        hasComponents: Boolean(item.has_components), parentRelationship: item.parent_relationship ?? null,
        components: item.item_components ?? [],
        sellingPlan: item.selling_plan_allocation?.selling_plan?.id ?? null});
      lines.set(key, (lines.get(key) || 0) + item.quantity);
    }
    return canonical({token: cart.token, currency: cart.currency, total: cart.total_price,
      // Shopify may echo private attributes after a write but omits them from cart.js.
      // Preserve outgoing metadata and compare only attributes available to cart readers.
      note: cart.note ?? '', attributes: Object.fromEntries(Object.entries(cart.attributes ?? {})
        .filter(([name]) => !name.startsWith('__'))), discounts: cart.discount_codes ?? [],
      lines: [...lines].sort(([a], [b]) => a.localeCompare(b))});
  }

  // Shippable units per variant, in the canonical order the rate callback checks.
  function manifestFor(cart) {
    const units = new Map();
    for (const item of cart.items.filter(shippableLine)) {
      const variant = String(item.variant_id);
      units.set(variant, (units.get(variant) || 0) + item.quantity);
    }
    if (!units.size) return null;
    if ([...units.values()].some(quantity => quantity > 10000)) throw new Error('dispatch_unavailable');
    const manifest = 'v1:' + [...units].sort(([a], [b]) => compareVariants(a, b))
      .map(([variant, quantity]) => variant + ':' + quantity).join(',');
    if (manifest.length > 2048) throw new Error('dispatch_unavailable');
    return manifest;
  }

  // The dispatch properties every shippable line should carry: the customer's
  // new choice, or the choice already on the cart with a manifest that matches
  // its current contents. A choice is never dropped or changed here.
  function dispatchTarget(cart, choice) {
    let policy = policies.includes(choice) ? choice : null;
    if (!policy) {
      const values = [...new Set(cart.items.filter(shippableLine).map(item => item.properties?.[dispatchProperty])
        .filter(value => value !== undefined))];
      if (values.length > 1 || (values.length && !policies.includes(values[0]))) throw new Error('dispatch_conflict');
      policy = values[0] ?? null;
    }
    const manifest = policy ? manifestFor(cart) : null;
    return policy && manifest ? {policy, manifest} : null;
  }

  function dispatchStale(cart) {
    const target = dispatchTarget(cart);
    return Boolean(target) && cart.items.some(item => shippableLine(item)
      && (item.properties?.[dispatchProperty] !== target.policy || item.properties?.[manifestProperty] !== target.manifest));
  }

  async function prepareCart({read, change, uuid}, initial, choice) {
    let cart = validateCart(initial || await read());
    // Shopify owns native component expansion. Parent references do not survive it.
    // Ordinary lines still retain their reference; incomplete callback history stays unverified.
    const shippable = cart.items.filter(shippableLine);
    if (!shippable.length) {
      const fresh = validateCart(await read());
      if (signature(fresh, true) !== signature(cart, true)) throw new Error('cart_changed');
      return fresh;
    }
    const references = [...new Set(shippable.map(item => item.properties?.[property]).filter(Boolean))];
    const reference = references.length === 1 && pattern.test(references[0]) ? references[0] : uuid();
    if (!pattern.test(reference)) throw new Error('invalid_reference');
    const target = dispatchTarget(cart, choice);
    const outdated = line => line.properties?.[property] !== reference || Boolean(target
      && (line.properties?.[dispatchProperty] !== target.policy || line.properties?.[manifestProperty] !== target.manifest));
    const original = signature(cart, false);
    for (let attempt = 0; attempt <= 100; attempt++) {
      const fresh = validateCart(await read());
      if (signature(fresh, true) !== signature(cart, true)) throw new Error('cart_changed');
      cart = fresh;
      const item = cart.items.find(line => shippableLine(line) && outdated(line));
      if (!item) return cart;
      if (attempt === 100) throw new Error('cart_changed');
      cart = validateCart(await change({id: item.key, quantity: item.quantity,
        properties: {...item.properties, [property]: reference,
          ...(target ? {[dispatchProperty]: target.policy, [manifestProperty]: target.manifest} : {})}}));
      if (signature(cart, false) !== original) throw new Error('cart_changed');
    }
    throw new Error('cart_changed');
  }

  function createCartRequest({basePath, fetch: fetcher, signal}) {
    const request = async (path, body) => {
      const response = await fetcher(basePath.replace(/\/$/, '') + path, {
        method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error',
        headers: {'Accept': 'application/json', ...(body ? {'Content-Type': 'application/json'} : {})},
        ...(body ? {body: JSON.stringify(body)} : {}), signal,
      });
      if (!response.ok) throw new Error('request_failed');
      const result = validateCart(await response.json());
      if (!body) return result;
      // A mutation response can include shipping from an earlier checkout in total_price.
      // Read the authoritative cart subtotal; preserve every other mutation-response check.
      const current = await request('.js');
      // Ajax mutation responses omit has_components even for native bundle parents.
      // Only fill that omitted marker from the same keyed line in the fresh cart.
      // Explicit disagreements and before/after bundle changes still fail their guards.
      const items = result.items.map(item => {
        if (Object.hasOwn(item, 'has_components')) return item;
        const matches = current.items.filter(line => line.key === item.key);
        if (matches.length !== 1) throw new Error('cart_changed');
        return {...item, has_components: matches[0].has_components};
      });
      if (signature({...result, items, total_price: current.total_price}, true) !== signature(current, true)) {
        throw new Error('cart_changed');
      }
      return current;
    };
    return request;
  }

  window.ModeCheckoutReference = {prepareCart, signature, validateCart, createCartRequest, manifestFor, dispatchStale};
  if (!window.document) return;

  const cartForm = () => document.querySelector('form[data-mode-shipping-checkout]');
  const cartPath = form => {
    const url = new URL(form.action, window.location.href);
    return url.origin === window.location.origin && /\/cart\/?$/.test(url.pathname) ? url.pathname : null;
  };

  // The cart renders the choice saved on its lines, or "together", as checked.
  // The customer's latest choice in this tab wins over both, so it survives
  // the theme redrawing the cart after each edit.
  let tabChoice = null;
  function rememberedChoice() {
    if (policies.includes(tabChoice)) return tabChoice;
    try {
      const saved = window.sessionStorage.getItem(storageKey);
      return policies.includes(saved) ? saved : null;
    } catch (error) { return null; }
  }

  function restoreChoice() {
    const choiceSet = cartForm()?.querySelector('[data-mode-dispatch-choice]');
    const saved = choiceSet ? rememberedChoice() : null;
    const input = saved ? choiceSet.querySelector('input[type="radio"][value="' + saved + '"]') : null;
    if (input && !input.checked) input.checked = true;
  }

  document.addEventListener('change', function (event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio' || !input.closest('[data-mode-dispatch-choice]')) return;
    tabChoice = input.value;
    try { window.sessionStorage.setItem(storageKey, input.value); } catch (error) { /* tabChoice still holds it */ }
  });

  // The checkout button shows progress. The status line announces it and is
  // only shown for something the customer has to act on.
  function showStatus(panel, message, visible) {
    panel.hidden = false;
    panel.classList.toggle('visually-hidden', !visible);
    panel.querySelector('[data-mode-shipping-message]').textContent = message;
  }
  function setButtonBusy(button, label) {
    if (button.dataset.modeLabel === undefined) button.dataset.modeLabel = button.textContent;
    button.textContent = label;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('mode-dispatch-busy');
  }
  function resetButton(button) {
    if (button.dataset.modeLabel === undefined) return;
    button.textContent = button.dataset.modeLabel;
    delete button.dataset.modeLabel;
    button.removeAttribute('aria-busy');
    button.classList.remove('mode-dispatch-busy');
  }

  // Checkout normally opens straight away. The "reload your cart" fallback only
  // appears if this page is still showing a while later, and never on a page
  // restored by going back from checkout.
  let readyTimer = null;
  window.addEventListener('pagehide', () => clearTimeout(readyTimer));
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    const form = cartForm();
    const button = form?.querySelector('button[name="checkout"]');
    const panel = form?.querySelector('[data-mode-shipping-status]');
    if (button) { resetButton(button); delete button.dataset.modeWholesaleBusy; }
    if (panel) panel.hidden = true;
  });

  // A cart edited after a choice was saved keeps that choice, with a manifest
  // for its new contents, so a checkout reopened without this form is still
  // priced for the goods actually in the cart.
  let syncTimer = null;
  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncDispatch, 500);
  }
  async function syncDispatch() {
    const form = cartForm();
    const basePath = form && cartPath(form);
    if (!basePath || form.dataset.modeShippingBusy === 'true') return;
    if (document.getElementById('ajaxBusy')?.style.display === 'block') { scheduleSync(); return; }
    // One update per edit: never loop if the cart cannot be brought up to date.
    try {
      if (Date.now() - Number(window.sessionStorage.getItem(storageKey + '-synced') || 0) < 10000) return;
    } catch (error) { return; }
    const panel = form.querySelector('[data-mode-shipping-status]');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    const request = createCartRequest({basePath, fetch, signal: controller.signal});
    let claimed = false;
    let rewritten = false;
    try {
      const cart = await request('.js');
      if (!dispatchStale(cart) || form.dataset.modeShippingBusy === 'true') return;
      claimed = true;
      form.dataset.modeShippingBusy = 'true';
      form.inert = true;
      if (panel) showStatus(panel, JSON.parse(panel.querySelector('[data-mode-shipping-copy]').textContent).updating, true);
      await prepareCart({read: () => request('.js'), change: body => request('/change.js', body), uuid: () => crypto.randomUUID()}, cart);
      rewritten = true;
      window.sessionStorage.setItem(storageKey + '-synced', String(Date.now()));
      // Line keys change with their properties; redraw the cart as it now is
      // with the theme's own cart redraw (assets/script.cart.js).
      if (typeof window.CartReload !== 'function') throw new Error('no_redraw');
      const response = await fetch(basePath, {credentials: 'same-origin', cache: 'no-store', signal: controller.signal});
      if (!response.ok) throw new Error('request_failed');
      window.CartReload(await response.text());
    } catch (error) {
      // The lines changed but the page still shows their old keys.
      if (rewritten) { window.location.reload(); return; }
      // Checkout rewrites the same properties before leaving the cart.
      if (panel && claimed) panel.hidden = true;
    } finally {
      clearTimeout(timer);
      if (claimed) {
        form.inert = false;
        delete form.dataset.modeShippingBusy;
      }
    }
  }

  function watchCart() {
    const form = cartForm();
    if (!form) return;
    restoreChoice();
    scheduleSync();
    new MutationObserver(() => { restoreChoice(); scheduleSync(); }).observe(form, {childList: true, subtree: true});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCart);
  else watchCart();

  async function prepareCheckout(form, submitter, proceed) {
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-mode-shipping-checkout]')
      || !submitter || submitter.name !== 'checkout' || submitter.disabled || submitter.dataset.modeWholesaleBusy === 'submitted' || !form.reportValidity()
      || form.dataset.modeShippingBusy === 'true') return false;
    const panel = form.querySelector('[data-mode-shipping-status]');
    if (!panel) return false;
    const reload = panel.querySelector('[data-mode-shipping-reload]');
    const copy = JSON.parse(panel.querySelector('[data-mode-shipping-copy]').textContent);
    clearTimeout(readyTimer);
    // A cart with goods sent from two places asks the customer how to send it.
    const choiceSet = form.querySelector('[data-mode-dispatch-choice]');
    const choice = choiceSet?.querySelector('input[type="radio"]:checked')?.value;
    if (choiceSet && !policies.includes(choice)) {
      showStatus(panel, copy.choose, true);
      reload.hidden = true;
      choiceSet.querySelector('input[type="radio"]')?.focus();
      return false;
    }
    const url = new URL(form.action, window.location.href);
    if (url.origin !== window.location.origin || !/\/cart\/?$/.test(url.pathname)) return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30000);
    const request = createCartRequest({basePath: url.pathname, fetch, signal: controller.signal});
    const metadata = {};
    for (const [name, value] of new FormData(form)) {
      if (typeof value !== 'string') continue;
      if (name === 'note') metadata.note = value;
      const attribute = /^attributes\[([^\]]+)\]$/.exec(name);
      if (attribute) {
        metadata.attributes ||= Object.create(null);
        metadata.attributes[attribute[1]] = value;
      }
    }
    const quantityFields = Array.from(form.querySelectorAll('input.item-qty'));
    form.dataset.modeShippingBusy = 'true';
    showStatus(panel, copy.preparing, false);
    reload.hidden = true;
    setButtonBusy(submitter, copy.preparingButton);
    form.inert = true;
    let failed = false;
    try {
      if (document.getElementById('ajaxBusy')?.style.display === 'block') throw new Error('cart_changed');
      let cart = await request('.js');
      for (const field of quantityFields) {
        const key = field.id.replace(/^updates_/, '');
        if (!cart.items.some(item => item.key === key && item.quantity === Number(field.value))) throw new Error('cart_changed');
      }
      const baseline = panel.querySelector('[data-mode-shipping-lines]');
      const expected = JSON.parse(baseline.textContent);
      const rendered = cart.items.map(item => ({key: item.key, quantity: item.quantity}));
      if (canonical(expected) !== canonical(rendered)) throw new Error('cart_changed');
      if (Object.keys(metadata).length) {
        const before = cart;
        cart = await request('/update.js', metadata);
        if (signature({...before, note: cart.note, attributes: cart.attributes}, true) !== signature(cart, true)) throw new Error('cart_changed');
      }
      const ready = await prepareCart({read: () => request('.js'), change: body => request('/change.js', body),
        uuid: () => crypto.randomUUID()}, cart, choice);
      if (!ready.items.length || !form.isConnected || !submitter.isConnected || submitter.disabled || !form.reportValidity()) {
        throw new Error('cart_changed');
      }
      if (proceed) {
        await proceed(ready);
      } else {
        const savedNames = quantityFields.map(field => [field, field.getAttribute('name')]);
        for (const [field] of savedNames) field.removeAttribute('name');
        form.inert = false;
        form.dataset.modeShippingReplay = 'true';
        try {
          form.requestSubmit(submitter);
        } finally {
          delete form.dataset.modeShippingReplay;
          for (const [field, name] of savedNames) if (name !== null) field.setAttribute('name', name);
        }
        readyTimer = setTimeout(function () {
          resetButton(submitter);
          showStatus(panel, copy.ready, true);
          reload.hidden = false;
        }, 6000);
      }
      return true;
    } catch (error) {
      failed = true;
      resetButton(submitter);
      showStatus(panel, error?.message === 'dispatch_unavailable' ? copy.dispatchUnavailable : copy.retry, true);
      reload.hidden = false;
      return false;
    } finally {
      clearTimeout(timer);
      form.inert = false;
      delete form.dataset.modeShippingBusy;
      if (failed) panel.focus();
    }
  }
  window.ModeCheckoutReference.prepareCheckout = prepareCheckout;
  document.addEventListener('submit', async function (event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-mode-shipping-checkout]')
      || event.submitter?.name !== 'checkout' || event.defaultPrevented || form.dataset.modeShippingReplay === 'true') return;
    event.preventDefault();
    await prepareCheckout(form, event.submitter);
  });
})();
